/**
 * Trigger matrix (REQ-PRICE-6/7; research/04 §4): ChangeEvent = synchronous
 * ProcessConditionalElements + UpdatePrice (plus imageddlChange for
 * "ImageSelector"); PriceUpdation (500 ms) and SliderPriceUpdation (50 ms)
 * are trailing-edge debounces calling ONLY UpdatePrice — debounced paths
 * never re-run conditional visibility (§7.20). firstLoad: findProductForm
 * first (THROWS TypeError when no form — research/02 §6 step 17), initial
 * image swap for checked swatch radio / data-showimage select, then
 * ProcessConditionalElements → UpdatePrice → RunNovaCurrencyConversionLogic
 * → PreventCheckoutOnEnter → the public `cpc:ready` lifecycle event
 * (MAINTAINABILITY-PLAN M6, issue #37/#122).
 *
 * (SliderInput stays in elements/slider.ts per the Phase-3 convention —
 * re-exported from the elements surface.)
 */
import { appState } from "../state/app-state";
import { PreventCheckoutOnEnter } from "../common/common-utils";
import { findProductForm } from "../checkout";
import { RunNovaCurrencyConversionLogic } from "../currency";
import { UpdatePrice } from "./update-price";
import {
  hasNumericConditionalTrigger,
  ProcessConditionalElements,
  resetNumericConditionalTriggerCache,
} from "./conditional";
import { imageddlChange } from "./image-swap";

/**
 * Public lifecycle hook (issue #122; MAINTAINABILITY-PLAN M6, #37): lets
 * theme/shim code that needs to gate its own behavior — e.g. blocking Add to
 * Cart until the calculator has attached its own ATC handling — know exactly
 * when that has happened, instead of guessing with a timer of its own. The
 * cpc-app-embed boot script used to dispatch a same-named event as soon as
 * calculator.js finished LOADING (`script.onload`), which is not the same
 * moment: the bundle can still be mid-render (fetching config, building the
 * DOM, wiring ATC) for a further stretch after that. A shim listening with
 * `{ once: true }` for the embed's event would unblock ATC before the
 * calculator was actually ready — precisely the race this event exists to
 * close (KITO GmbH, Crisp session 4c27671f, CPC HQ ticket #234). This is now
 * the single, authoritative `cpc:ready` dispatch site; the embed no longer
 * fires one of its own.
 */
function notifyCalculatorReady(): void {
  try {
    if (typeof window === "undefined" || typeof (window as any).CustomEvent !== "function") return;
    window.dispatchEvent(
      new CustomEvent("cpc:ready", {
        detail: { shop: appState.cpc_shopUrl, calcId: appState.cpc_calculatorId },
      }),
    );
  } catch (e) {
    // never let a hostile/old browser environment break the render pipeline
  }
}

export function firstLoad(addToCartSelector: any): void {
  const { productForm: foundProductForm, submitButton } = findProductForm(addToCartSelector);

  let productForm = foundProductForm;
  var radio = productForm.querySelector("input[type='radio']:checked");
  var dropDownImageSelector = productForm.querySelector(
    "select[data-showimage='true']",
  );
  if (radio !== undefined && radio !== null && radio.offsetParent !== null) {
    imageddlChange(radio);
  } else if (
    dropDownImageSelector !== undefined &&
    dropDownImageSelector !== null &&
    dropDownImageSelector.offsetParent !== null
  ) {
    imageddlChange(dropDownImageSelector);
  }
  // #151: the calculator DOM has just been (re)built, so the cached "does this calculator have
  // a numeric trigger" answer from any previous render must not survive into this one.
  resetNumericConditionalTriggerCache();
  ProcessConditionalElements();
  UpdatePrice();
  RunNovaCurrencyConversionLogic();
  PreventCheckoutOnEnter();
  notifyCalculatorReady();
}

/*Every element change event will trigger this method */
export function ChangeEvent(elementType: any, domElement: any = null): void {
  if (elementType === "ImageSelector") {
    imageddlChange(domElement);
  }
  ProcessConditionalElements();
  UpdatePrice();
}

function debounce(func: any, wait: any, immediate?: any): (...args: any[]) => void {
  var timeout: any;
  return function (this: any) {
    var context = this,
      args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

// Hoisted function declarations (not `const debounce(...)` values): pricing →
// checkout (findProductForm) → validate-inputs → elements closes a module
// cycle, and elements/build-calculator assigns window.PriceUpdation at ITS
// module-eval time — which the cycle can order BEFORE this module's body runs
// (a `const` here → TDZ ReferenceError at boot). Function declarations are
// initialized at instantiation, so the eval-time read always succeeds; the
// debounce instance is created on first call (unobservable — the first call
// is always at runtime, long after every module body has run).
var priceUpdationDebounced: ((...args: any[]) => void) | null = null;
export function PriceUpdation(this: any, ...args: any[]): void {
  if (priceUpdationDebounced === null) {
    priceUpdationDebounced = debounce(function (element: any) {
      // #151: visibility on the DEBOUNCED edge, not per keystroke — re-running it on every key
      // would flash a dependent element on "1" on the way to "15". Gated so a calculator with no
      // numeric trigger never runs the sweep while the shopper types, exactly as before.
      if (hasNumericConditionalTrigger()) ProcessConditionalElements();
      UpdatePrice();
    }, 500);
  }
  priceUpdationDebounced.apply(this, args);
}

// Slider drag fires continuously; recalc on a short ~50ms debounce so the price
// tracks the drag without hammering the formula engine on every pixel (CPCHQ #13).
var sliderPriceUpdationDebounced: ((...args: any[]) => void) | null = null;
export function SliderPriceUpdation(this: any, ...args: any[]): void {
  if (sliderPriceUpdationDebounced === null) {
    sliderPriceUpdationDebounced = debounce(function (element: any) {
      // #151: same gate as PriceUpdation — a slider can control a numeric condition too.
      if (hasNumericConditionalTrigger()) ProcessConditionalElements();
      UpdatePrice();
    }, 50);
  }
  sliderPriceUpdationDebounced.apply(this, args);
}
